import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";

const LocationHistory = ({
    asset,
    isEditing,
    onUpdate
}) => {

    const [showFloorPlan, setShowFloorPlan] = useState(false);

    const locationOptions = [
        { value: "Building A - Floor 1", label: "Building A - Floor 1" },
        { value: "Building A - Floor 2", label: "Building A - Floor 2" },
        { value: "Building B - Floor 1", label: "Building B - Floor 1" },
        { value: "Warehouse", label: "Warehouse" }
    ];

    const roomOptions = [
        { value: "IT Office", label: "IT Office" },
        { value: "Conference Room", label: "Conference Room" },
        { value: "Server Room", label: "Server Room" },
        { value: "Storage Room", label: "Storage Room" }
    ];

    // =========================================
    // DATABASE DATA
    // =========================================

    const locationHistory = asset?.location_histories || asset?.locationHistories || [];

    // =========================================
    // CURRENT LOCATION
    // =========================================

    const currentLocation =
        locationHistory.find(item => item.is_current) || {
            location: asset?.location || "",
            room: asset?.room || "",
            moved_by: asset?.owner?.name || "System",
            created_at: asset?.created_at || ""
        };

    const combinedLocationOptions = [
        ...locationOptions,
        ...(currentLocation.location && !locationOptions.some(o => o.value === currentLocation.location)
            ? [{ value: currentLocation.location, label: currentLocation.location }]
            : []),
        ...(asset?.location && asset.location !== currentLocation.location && !locationOptions.some(o => o.value === asset.location)
            ? [{ value: asset.location, label: asset.location }]
            : [])
    ];

    // =========================================
    // UPDATE
    // =========================================

    const handleLocationChange = (field, value) => {

        const updatedAsset = {

            ...asset,

            [field]: value
        };

        onUpdate(updatedAsset);
    };

    // =========================================
    // DATE FORMAT
    // =========================================

    const formatDate = (date) => {

        if (!date) return "N/A";

        return new Date(date).toLocaleString("en-US", {

            year: "numeric",

            month: "short",

            day: "numeric",

            hour: "2-digit",

            minute: "2-digit"
        });
    };

    return (

        <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">

            {/* Header */}

            <div className="flex items-center justify-between mb-6">

                <h3 className="text-lg font-semibold">

                    Location & History

                </h3>

                <Button

                    variant="outline"

                    size="sm"

                    iconName={showFloorPlan ? "EyeOff" : "Eye"}

                    iconPosition="left"

                    onClick={() => setShowFloorPlan(!showFloorPlan)}

                >

                    {showFloorPlan ? "Hide" : "Show"} Floor Plan

                </Button>

            </div>

            {/* Current Location */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <div>

                    <h4 className="font-medium mb-4">

                        Current Location

                    </h4>

                    <Select

                        label="Building / Floor"

                        options={combinedLocationOptions}

                        value={currentLocation.location}

                        disabled={!isEditing}

                        onChange={(value) =>
                            handleLocationChange(
                                "location",
                                value
                            )
                        }

                    />

                    <div className="mt-4">

                        <Select

                            label="Room"

                            options={roomOptions}

                            value={currentLocation.room}

                            disabled={!isEditing}

                            onChange={(value) =>
                                handleLocationChange(
                                    "room",
                                    value
                                )
                            }

                        />

                    </div>

                    <div className="mt-6 bg-muted rounded-lg p-4">

                        <p>

                            <strong>Building :</strong>{" "}

                            {currentLocation.location || "N/A"}

                        </p>

                        <p>

                            <strong>Room :</strong>{" "}

                            {currentLocation.room || "N/A"}

                        </p>

                        <p>

                            <strong>Updated By :</strong>{" "}

                            {currentLocation.moved_by || "System"}

                        </p>

                        <p>

                            <strong>Updated At :</strong>{" "}

                            {formatDate(currentLocation.created_at)}

                        </p>

                    </div>

                </div>

                {/* Floor Plan */}

                <div>

                    {

                        showFloorPlan && (

                            <div className="bg-muted rounded-lg h-64 flex items-center justify-center">

                                <div className="text-center">

                                    <Icon

                                        name="Map"

                                        size={50}

                                        className="mx-auto mb-3 text-primary"

                                    />

                                    <p>

                                        Floor Plan Preview

                                    </p>

                                </div>

                            </div>

                        )

                    }

                </div>

            </div>

            {/* History */}

            <div className="mt-8">

                <h4 className="font-medium mb-4">

                    Movement History

                </h4>

                <div className="overflow-x-auto">

                    <table className="w-full text-sm">

                        <thead>

                            <tr className="border-b border-border text-muted-foreground">

                                <th className="text-left p-3">Date</th>

                                <th className="text-left p-3">Location</th>

                                <th className="text-left p-3">Room</th>

                                <th className="text-left p-3">Updated By</th>

                                <th className="text-left p-3">Reason</th>

                                <th className="text-left p-3">Status</th>

                            </tr>

                        </thead>

                        <tbody>

                            {

                                locationHistory.length > 0 ? (

                                    locationHistory.map(item => (

                                        <tr
                                            key={item.id}
                                            className="border-b border-border/60 hover:bg-muted/30 transition-colors"
                                        >
                                            <td className="p-3">
                                                {formatDate(item.created_at)}
                                            </td>
                                            <td className="p-3 font-medium text-foreground">
                                                {item.location}
                                            </td>
                                            <td className="p-3">
                                                {item.room}
                                            </td>
                                            <td className="p-3">
                                                {item.moved_by || "System"}
                                            </td>
                                            <td className="p-3 text-muted-foreground">
                                                {item.reason || "—"}
                                            </td>
                                            <td className="p-3">
                                                {item.is_current ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-success/15 text-success">
                                                        Current
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-muted-foreground bg-muted">
                                                        History
                                                    </span>
                                                )}
                                            </td>
                                        </tr>

                                    ))

                                ) : (

                                    <tr>

                                        <td

                                            colSpan={6}

                                            className="text-center py-6 text-muted-foreground"

                                        >

                                            No location history found

                                        </td>

                                    </tr>

                                )

                            }

                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

};

export default LocationHistory;